import type React from 'react';
import {useId, useMemo, useRef, useState} from 'react';
import {
  CalendarDays,
  ChevronLeft,
  ClipboardList,
  Paperclip,
  Plus,
  StickyNote,
  Upload,
  X,
} from 'lucide-react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {cn} from '@/matic/lib/utils/cn';
import {Skeleton} from '@/matic/components/ui/skeleton';
import {useAppContext} from '@/matic/providers/AppProvider';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/matic/components/ui/dialog';
import {Button} from '@/matic/components/ui/button';
import {Tooltip} from '@/matic/components/ui/tooltip';
import {DatePicker} from '@/matic/components/ui/date-picker';
import {useToast} from '@/matic/components/ui/toast';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {useObjectType} from '@/matic/features/master-data/hooks/useObjectType';
import {
  useObjectRecordMutations,
  extractFieldErrors,
} from '@/matic/features/master-data/hooks/useObjectRecordMutations';
import {ObjectRecordForm} from '@/matic/features/master-data/components/ObjectRecordForm';
import {
  TRIBAL_KNOWLEDGE_FIELD_KEY,
  TRIBAL_KNOWLEDGE_FIELD_LABEL,
} from '@/matic/features/master-data/constants';
import type {ObjectType} from '@/matic/features/master-data/types/master-data-types';
import {useObjectTypeMutations} from '@/matic/features/master-data/hooks/useObjectTypeMutations';
import {getFilesApi} from '@/matic/features/files/api/files-api';
import {filesKeys} from '@/matic/features/files/query-keys';
import {relayKeys} from '@/matic/features/relay/query-keys';
import {getRelayApi} from '@/matic/features/relay/api/relay-api';
import {
  formatNoteDateChip,
  noteDateToday,
  noteDateToIso,
} from '@/matic/features/relay/utils/relay-utils';
import {
  AssociationSelection,
  useAssociationSelection,
  type SuggestedAssociation,
} from '@/matic/features/relay/components/shared/AssociationSelection';
import {useScopeEligibleRecordTypes} from '@/matic/features/relay/hooks/useScopeEligibleRecordTypes';
import {getRecordTypesApi} from '@/matic/features/records/api/record-types-api';
import {useRecordMutations} from '@/matic/features/records/hooks/useRecordMutations';
import {buildScopedRecordInput} from '@/matic/features/records/lib/build-scoped-record-input';
import type {RecordType} from '@/matic/features/records/types';

export type CreateDialogContextAssociation = {
  objectTypeId: string;
  objectTypeName: string;
  /** Null = type-only ("Category") context, no specific record. */
  objectRecordId: string | null;
  objectRecordLabel: string;
};

type Step =
  | {kind: 'pickType'}
  | {kind: 'note'}
  | {kind: 'record'; typeId: string; typeName: string}
  | {kind: 'recordTypePicker'}
  | {kind: 'qmsRecord'; recordType: RecordType}
  | {kind: 'newType'};

type Props = {
  open: boolean;
  onClose: () => void;
  types: ObjectType[];
  contextAssociations: CreateDialogContextAssociation[];
  /**
   * Files dropped or picked in the picker step's quick-drop zone. The caller
   * owns the actual upload (shared `useQuickDropUpload` + tray, so drops from
   * the FAB's global drag-and-drop and drops from this dialog report to the
   * same tray) — this dialog just closes itself once files are handed off.
   */
  onQuickDrop: (files: File[]) => void;
  /**
   * Skip the type-picker and open straight into one step — the global FAB's
   * "Create a note" card opens this dialog scoped to just that flow (its own
   * popover already narrowed the choice to Note vs. Upload, so re-showing the
   * full Note/Record/Object picker here would undo that), and the Scope
   * Picker's "New type…" / "New {type}…" rows open straight into those forms.
   * When set, the step's "Back" closes the dialog instead of revealing the
   * picker.
   */
  initialStep?:
    | 'note'
    | {kind: 'newType'}
    | {kind: 'record'; typeId: string; typeName: string};
};

function resolveInitialStep(initialStep: Props['initialStep']): Step {
  if (initialStep === undefined) return {kind: 'pickType'};
  if (initialStep === 'note') return {kind: 'note'};
  return initialStep;
}

export function RelayCreateDialog({
  open,
  onClose,
  types,
  contextAssociations,
  onQuickDrop,
  initialStep,
}: Props) {
  const [step, setStep] = useState<Step>(() => resolveInitialStep(initialStep));

  const handleClose = () => {
    setStep(resolveInitialStep(initialStep));
    onClose();
  };

  const handleBack = () => {
    if (initialStep) {
      handleClose();
      return;
    }

    setStep({kind: 'pickType'});
  };

  const title =
    step.kind === 'pickType'
      ? 'Create'
      : step.kind === 'note'
        ? 'New note'
        : step.kind === 'newType'
          ? 'New category'
          : step.kind === 'recordTypePicker'
            ? 'New record'
            : step.kind === 'qmsRecord'
              ? `New ${step.recordType.name.toLowerCase()}`
              : `New ${step.typeName.toLowerCase()}`;

  // MDM object-record creation is the same form as ObjectRecordCreateModal
  // (admin) — match its width so the two surfaces have the same shape.
  // Every other step is a lighter-weight quick-create and stays compact.
  const dialogSize = step.kind === 'record' ? 'xl' : 'md';

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent className="p-0" size={dialogSize}>
        <DialogHeader className="mb-0 flex flex-row items-center justify-between border-b border-neutral-100 px-6 py-4">
          <DialogTitle className="text-lg font-semibold text-neutral-900">
            {title}
          </DialogTitle>
          <button
            type="button"
            onClick={handleClose}
            className="text-neutral-400 hover:text-neutral-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </DialogHeader>

        {step.kind === 'pickType' && (
          <TypePickerStep
            types={types}
            onQuickDrop={(files) => {
              onQuickDrop(files);
              handleClose();
            }}
            onPickNote={() => {
              setStep({kind: 'note'});
            }}
            onPickQmsRecord={() => {
              setStep({kind: 'recordTypePicker'});
            }}
            onPickRecord={(type) => {
              setStep({kind: 'record', typeId: type.id, typeName: type.name});
            }}
            onPickNewType={() => {
              setStep({kind: 'newType'});
            }}
          />
        )}
        {step.kind === 'recordTypePicker' && (
          <RecordTypePickerStep
            types={types}
            onBack={handleBack}
            onPick={(recordType) => {
              setStep({kind: 'qmsRecord', recordType});
            }}
          />
        )}
        {step.kind === 'qmsRecord' && (
          <QmsRecordFormStep
            recordType={step.recordType}
            contextAssociations={contextAssociations}
            onBack={() => {
              setStep({kind: 'recordTypePicker'});
            }}
            onClose={handleClose}
          />
        )}
        {step.kind === 'note' && (
          <NoteFormStep
            contextAssociations={contextAssociations}
            // No picker to return to when this dialog opened straight into
            // Note (initialStep) — Back would just duplicate Cancel/X.
            onBack={initialStep ? undefined : handleBack}
            onClose={handleClose}
          />
        )}
        {step.kind === 'record' && (
          <RecordFormStep
            typeId={step.typeId}
            typeName={step.typeName}
            onBack={initialStep ? undefined : handleBack}
            onClose={handleClose}
          />
        )}
        {step.kind === 'newType' && (
          <NewTypeFormStep
            onBack={initialStep ? undefined : handleBack}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Type picker step
// ---------------------------------------------------------------------------

type TypePickerStepProps = {
  types: ObjectType[];
  onQuickDrop: (files: File[]) => void;
  onPickNote: () => void;
  onPickQmsRecord: () => void;
  onPickRecord: (type: ObjectType) => void;
  onPickNewType: () => void;
};

/**
 * The dialog's default content: a zero-question drop zone leads (drop or
 * click → upload starts immediately, no form), with the other creation
 * kinds — genuinely form-shaped, so they keep their picker+form flow —
 * demoted to a compact row underneath.
 */
function TypePickerStep({
  types,
  onQuickDrop,
  onPickNote,
  onPickQmsRecord,
  onPickRecord,
  onPickNewType,
}: TypePickerStepProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  return (
    <div className="px-4 py-3">
      <button
        type="button"
        onClick={() => {
          inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragActive(true);
        }}
        onDragLeave={() => {
          setIsDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragActive(false);
          onQuickDrop([...e.dataTransfer.files]);
        }}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors',
          isDragActive
            ? 'border-primary-400 bg-primary-50'
            : 'border-neutral-300 bg-neutral-50 hover:border-neutral-400 hover:bg-neutral-100'
        )}
      >
        <Upload className="h-6 w-6 text-neutral-400" />
        <p className="text-sm font-medium text-neutral-700">
          Drop files here, or click to upload
        </p>
        <p className="text-xs text-neutral-400">
          MATIC will classify and file them automatically
        </p>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          onQuickDrop([...(e.target.files ?? [])]);
          e.target.value = '';
        }}
      />

      <div className="mx-1 mt-3 border-t border-neutral-100 pt-2">
        <PickerRow
          icon={<StickyNote className="h-3.5 w-3.5" />}
          label="Note"
          onClick={onPickNote}
        />
        <PickerRow
          icon={<ClipboardList className="h-3.5 w-3.5" />}
          label="Record"
          onClick={onPickQmsRecord}
        />

        {types.length > 0 && (
          <>
            <div className="mx-1 my-1 border-t border-neutral-100" />
            <p className="mb-0.5 px-3 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
              Categories
            </p>
            {types.map((type) => (
              <PickerRow
                key={type.id}
                icon={
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-neutral-100 text-[9px] font-semibold uppercase text-neutral-500">
                    {type.name[0]}
                  </span>
                }
                label={type.name}
                onClick={() => {
                  onPickRecord(type);
                }}
              />
            ))}
          </>
        )}

        <div className="mx-1 my-1 border-t border-neutral-100" />
        <PickerRow
          icon={<Plus className="h-3.5 w-3.5" />}
          label="New type…"
          muted
          onClick={onPickNewType}
        />
      </div>
    </div>
  );
}

type PickerRowProps = {
  icon: React.ReactNode;
  label: string;
  muted?: boolean;
  onClick: () => void;
};

function PickerRow({icon, label, muted, onClick}: PickerRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm transition-colors hover:bg-primary-50',
        muted ? 'text-neutral-400' : 'text-neutral-800'
      )}
    >
      <span
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center',
          muted ? 'text-neutral-300' : 'text-neutral-400'
        )}
      >
        {icon}
      </span>
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Shared: file attach section (used in both Note and File forms)
// ---------------------------------------------------------------------------

type FileAttachSectionProps = {
  file: File | null;
  onFileChange: (file: File | null) => void;
  compact?: boolean;
};

function FileAttachSection({
  file,
  onFileChange,
  compact,
}: FileAttachSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped) onFileChange(dropped);
  };

  if (file) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
        <div className="flex min-w-0 items-center gap-2 text-neutral-700">
          <Paperclip className="h-4 w-4 shrink-0 text-neutral-400" />
          <span className="truncate">{file.name}</span>
        </div>
        <button
          type="button"
          onClick={() => {
            onFileChange(null);
          }}
          className="shrink-0 text-neutral-400 hover:text-neutral-700"
          aria-label="Remove file"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={handleDrop}
        className={cn(
          'flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-neutral-300 bg-neutral-50 text-sm text-neutral-500 transition-colors hover:border-neutral-400 hover:bg-neutral-100',
          compact ? 'px-3 py-2' : 'px-4 py-5'
        )}
      >
        <Upload className="h-4 w-4 shrink-0" />
        {compact ? 'Attach a file' : 'Drop file here, or click to browse'}
      </button>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFileChange(f);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Note form step (note-primary, optional file attachment)
// ---------------------------------------------------------------------------

type NoteFormStepProps = {
  contextAssociations: CreateDialogContextAssociation[];
  /** Omitted when this dialog opened straight into this step (initialStep) —
   *  there's no picker to return to. */
  onBack?: () => void;
  onClose: () => void;
};

function NoteFormStep({
  contextAssociations,
  onBack,
  onClose,
}: NoteFormStepProps) {
  const tenantId = useRequiredTenantId();
  const queryClient = useQueryClient();
  const {toast} = useToast();
  const [body, setBody] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [createdDate, setCreatedDate] = useState<string>('');
  const assoc = useAssociationSelection({preset: contextAssociations});

  const submitMutation = useMutation({
    async mutationFn() {
      let fileId: string | undefined;
      if (attachedFile) {
        const filesApi = getFilesApi();
        const attachedContentType =
          attachedFile.type || 'application/octet-stream';
        const {url, fileId: id} = await filesApi.requestUploadUrl(
          attachedFile.name,
          attachedContentType,
          attachedFile.size
        );
        await filesApi.uploadToPresignedUrl(
          url,
          attachedFile,
          undefined,
          attachedContentType
        );
        const created = await filesApi.createFileFromStaging({
          fileId: id,
          name: attachedFile.name,
          originalFilename: attachedFile.name,
          contentType: attachedContentType,
          associations: assoc.selectedAssociations,
        });
        fileId = created.id;
        await queryClient.invalidateQueries({
          queryKey: filesKeys.files.listPrefix(tenantId),
        });
      }

      return getRelayApi().createNote({
        body: body.trim(),
        associations: assoc.selectedAssociations,
        ...(fileId ? {fileIds: [fileId]} : {}),
        ...(createdDate ? {createdAt: noteDateToIso(createdDate)} : {}),
      });
    },
    onSuccess() {
      void queryClient.invalidateQueries({queryKey: relayKeys.trace.all()});
      toast({title: 'Note created', variant: 'success'});
      onClose();
    },
    onError() {
      toast({title: 'Failed to create note', variant: 'error'});
    },
  });

  const canSubmit =
    body.trim().length > 0 &&
    !submitMutation.isPending &&
    assoc.selectedAssociations.length > 0;

  return (
    <>
      <div className="max-h-[60vh] space-y-4 overflow-y-auto px-6 py-5">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-0.5 text-xs text-neutral-400 hover:text-neutral-600"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Back
          </button>
        )}

        <textarea
          autoFocus
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
          }}
          placeholder="Note content…"
          rows={5}
          className="w-full resize-none rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />

        <FileAttachSection
          file={attachedFile}
          onFileChange={setAttachedFile}
          compact
        />

        <div className="flex items-center gap-2">
          <DatePicker
            value={createdDate}
            onChange={setCreatedDate}
            maxDate={noteDateToday()}
            className="w-auto"
            popoverAlign="left"
            triggerTitle={
              createdDate ? 'Change note date' : 'Backdate this note'
            }
            triggerContent={
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                {createdDate ? formatNoteDateChip(createdDate) : 'Add date'}
              </span>
            }
            buttonClassName={cn(
              'rounded-md border border-dashed px-2.5 py-1 text-xs font-medium transition-colors',
              createdDate
                ? 'border-primary-200 bg-primary-50 text-primary-700'
                : 'border-neutral-300 text-neutral-500 hover:border-neutral-400 hover:bg-neutral-100'
            )}
          />
          {createdDate && (
            <button
              type="button"
              aria-label="Clear date"
              className="text-neutral-400 hover:text-neutral-700"
              onClick={() => {
                setCreatedDate('');
              }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <AssociationSelection controller={assoc} />
      </div>

      <DialogFooter className="mt-0 border-t border-neutral-100 px-6 py-4">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        {assoc.selectedAssociations.length === 0 ? (
          <Tooltip content="Select an association to create a note">
            <Button variant="dark" size="sm" disabled>
              Create note
            </Button>
          </Tooltip>
        ) : (
          <Button
            variant="dark"
            size="sm"
            disabled={!canSubmit}
            loading={submitMutation.isPending}
            onClick={() => {
              submitMutation.mutate();
            }}
          >
            Create note
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

// ---------------------------------------------------------------------------
// MDM record form step
// ---------------------------------------------------------------------------

type RecordFormStepProps = {
  typeId: string;
  typeName: string;
  /** Omitted when this dialog opened straight into this step (initialStep) —
   *  there's no picker to return to. */
  onBack?: () => void;
  onClose: () => void;
};

function RecordFormStep({
  typeId,
  typeName,
  onBack,
  onClose,
}: RecordFormStepProps) {
  const formId = useId();
  const {data: objectType, isLoading} = useObjectType(typeId);
  const {createMutation} = useObjectRecordMutations(typeId);

  const serverErrors = useMemo(
    () =>
      createMutation.error
        ? extractFieldErrors(createMutation.error)
        : undefined,
    [createMutation.error]
  );

  return (
    <>
      <div className="max-h-[60vh] space-y-4 overflow-y-auto px-6 py-5">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-0.5 text-xs text-neutral-400 hover:text-neutral-600"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Back
          </button>
        )}

        {isLoading || !objectType ? (
          <div className="space-y-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-2/3" />
          </div>
        ) : (
          <ObjectRecordForm
            objectType={objectType}
            formId={formId}
            mode="create"
            collapseOptionalFields
            serverErrors={serverErrors}
            onValidSubmit={(payload) => {
              createMutation.mutate(payload, {
                onSuccess() {
                  onClose();
                },
              });
            }}
          />
        )}
      </div>

      <DialogFooter className="mt-0 border-t border-neutral-100 px-6 py-4">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={formId}
          variant="dark"
          size="sm"
          disabled={isLoading || !objectType || createMutation.isPending}
          loading={createMutation.isPending}
        >
          Create {typeName.toLowerCase()}
        </Button>
      </DialogFooter>
    </>
  );
}

// ---------------------------------------------------------------------------
// QMS record: record-type picker step
// ---------------------------------------------------------------------------

type RecordTypePickerStepProps = {
  types: ObjectType[];
  onBack: () => void;
  onPick: (recordType: RecordType) => void;
};

function RecordTypePickerStep({
  types,
  onBack,
  onPick,
}: RecordTypePickerStepProps) {
  const {toast} = useToast();
  const [loadingId, setLoadingId] = useState<string | null>(null);

  // The sidebar has no scope, so feed every object type — eligibility is any
  // published record type carrying an OBJECT_REFERENCE field pointing at one of
  // them. Fetch runs only while this step is mounted (lazy).
  const scopeRefs = useMemo(
    () => types.map((type) => ({typeId: type.id})),
    [types]
  );
  const {eligibleTypes, isLoading} = useScopeEligibleRecordTypes(scopeRefs);

  const handlePick = async (typeId: string) => {
    setLoadingId(typeId);
    try {
      // Eligible-type summaries omit field definitions, so fetch the full type
      // to resolve the OBJECT_REFERENCE fields + workflow steps.
      const recordType = await getRecordTypesApi().getRecordType(typeId);
      onPick(recordType);
    } catch {
      toast({title: 'Could not load record type', variant: 'error'});
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="space-y-3 px-6 py-5">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-0.5 text-xs text-neutral-400 hover:text-neutral-600"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Back
      </button>

      {isLoading ? (
        <div className="space-y-1.5">
          {Array.from({length: 4}).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : eligibleTypes.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-neutral-400">
          No record types reference your objects yet.
        </p>
      ) : (
        <div className="-mx-1">
          {eligibleTypes.map((recordType) => (
            <button
              key={recordType.id}
              type="button"
              disabled={loadingId !== null}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-neutral-800 transition-colors hover:bg-primary-50 disabled:opacity-50"
              onClick={() => {
                void handlePick(recordType.id);
              }}
            >
              <span className="shrink-0 font-mono text-xs text-neutral-400">
                {recordType.prefix}
              </span>
              <span className="truncate">{recordType.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// QMS record: create form step
// ---------------------------------------------------------------------------

type QmsRecordFormStepProps = {
  recordType: RecordType;
  contextAssociations: CreateDialogContextAssociation[];
  onBack: () => void;
  onClose: () => void;
};

function QmsRecordFormStep({
  recordType,
  contextAssociations,
  onBack,
  onClose,
}: QmsRecordFormStepProps) {
  const {userId} = useAppContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {createMutation} = useRecordMutations();

  // The object types this record type references — what the smart-select is
  // constrained to, and which preset chips survive.
  const allowedObjectTypeIds = useMemo(
    () => [
      ...new Set(
        recordType.fields
          .filter(
            (f) =>
              f.fieldType === 'object_reference' &&
              f.config?.referenceObjectTypeId
          )
          .map((f) => f.config!.referenceObjectTypeId!)
      ),
    ],
    [recordType]
  );
  const allowed = useMemo(
    () => new Set(allowedObjectTypeIds),
    [allowedObjectTypeIds]
  );

  const presetAssociations = useMemo(
    () => contextAssociations.filter((c) => allowed.has(c.objectTypeId)),
    [contextAssociations, allowed]
  );

  const assoc = useAssociationSelection({preset: presetAssociations});
  const [name, setName] = useState(`New ${recordType.name}`);

  const handleSubmit = async () => {
    const {input} = buildScopedRecordInput(
      recordType,
      // Type-only ("Category") selections carry no record to reference in
      // the new QMS record's object-reference fields — excluded.
      assoc.selectedAssociations
        .filter(
          (a): a is {objectTypeId: string; objectRecordId: string} =>
            a.objectRecordId !== null
        )
        .map((a) => ({
          typeId: a.objectTypeId,
          recordId: a.objectRecordId,
        })),
      userId
    );

    try {
      const record = await createMutation.mutateAsync({
        ...input,
        name: name.trim(),
      });
      // The new reference edge changes which records the trace returns —
      // bust trace so Library folders reflect the create.
      void queryClient.invalidateQueries({queryKey: relayKeys.trace.all()});
      onClose();
      navigate(`/records/${record.id}`);
    } catch {
      // CreateMutation surfaces its own error toast.
    }
  };

  const canSubmit =
    name.trim().length > 0 &&
    assoc.selectedAssociations.length > 0 &&
    !createMutation.isPending;

  return (
    <>
      <div className="max-h-[60vh] space-y-4 overflow-y-auto px-6 py-5">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-0.5 text-xs text-neutral-400 hover:text-neutral-600"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Back
        </button>

        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          placeholder={`${recordType.name} name`}
          className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />

        <AssociationSelection
          controller={assoc}
          allowedObjectTypeIds={allowedObjectTypeIds}
        />
      </div>

      <DialogFooter className="mt-0 border-t border-neutral-100 px-6 py-4">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="dark"
          size="sm"
          disabled={!canSubmit}
          loading={createMutation.isPending}
          onClick={() => {
            void handleSubmit();
          }}
        >
          Create {recordType.name.toLowerCase()}
        </Button>
      </DialogFooter>
    </>
  );
}

// ---------------------------------------------------------------------------
// New object type form step
// ---------------------------------------------------------------------------

const NEW_TYPE_FIELDS = [
  {
    label: 'Name',
    fieldKey: 'name',
    fieldType: 'text' as const,
    required: true,
    sortOrder: 1,
    showInList: true,
  },
  {
    label: TRIBAL_KNOWLEDGE_FIELD_LABEL,
    fieldKey: TRIBAL_KNOWLEDGE_FIELD_KEY,
    fieldType: 'text' as const,
    required: false,
    sortOrder: 2,
    showInList: false,
  },
];

type NewTypeFormStepProps = {
  /** Omitted when this dialog opened straight into this step (initialStep) —
   *  there's no picker to return to. */
  onBack?: () => void;
  onClose: () => void;
};

function NewTypeFormStep({onBack, onClose}: NewTypeFormStepProps) {
  const {toast} = useToast();
  const {createMutation} = useObjectTypeMutations();
  const [name, setName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [description, setDescription] = useState('');
  const [prefixEdited, setPrefixEdited] = useState(false);

  const derivePrefix = (n: string) =>
    n
      .replaceAll(/[^a-z\d]/gi, '')
      .slice(0, 4)
      .toUpperCase();

  const canSubmit =
    name.trim().length > 0 &&
    prefix.trim().length > 0 &&
    !createMutation.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    createMutation.mutate(
      {
        name: name.trim(),
        prefix: prefix.trim().toUpperCase(),
        ...(description.trim() ? {description: description.trim()} : {}),
        fields: NEW_TYPE_FIELDS,
        displayFields: ['name'],
      },
      {
        onSuccess() {
          onClose();
        },
        onError() {
          toast({title: 'Failed to create category', variant: 'error'});
        },
      }
    );
  };

  return (
    <>
      <div className="space-y-3 px-6 py-5">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-0.5 text-xs text-neutral-400 hover:text-neutral-600"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Back
          </button>
        )}

        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!prefixEdited) setPrefix(derivePrefix(e.target.value));
          }}
          placeholder="Type name (e.g. Supplier)"
          className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />

        <div>
          <input
            type="text"
            value={prefix}
            onChange={(e) => {
              setPrefixEdited(true);
              setPrefix(
                e.target.value.toUpperCase().replaceAll(/[^A-Z\d]/g, '')
              );
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) handleSubmit();
            }}
            placeholder="Prefix (e.g. SUP)"
            maxLength={8}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <p className="mt-1 text-xs text-neutral-400">
            Used to generate record IDs — e.g. SUP-001
          </p>
        </div>

        <textarea
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
          placeholder="Description (optional)"
          rows={2}
          className="w-full resize-none rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      <DialogFooter className="mt-0 border-t border-neutral-100 px-6 py-4">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="dark"
          size="sm"
          disabled={!canSubmit}
          loading={createMutation.isPending}
          onClick={handleSubmit}
        >
          Create type
        </Button>
      </DialogFooter>
    </>
  );
}
